/**
 * 规则注册表 - 管理所有验证规则
 */

import type { Rule, RuleConfig } from '../types';
import { Logger } from '../utils/logger';

/**
 * 规则注册表类
 */
class RuleRegistry {
  private rules: Map<string, Rule> = new Map();
  private configs: Map<string, RuleConfig> = new Map();
  private logger: Logger;

  constructor() {
    this.logger = new Logger();
  }

  /**
   * 注册新规则
   */
  register(rule: Rule): void {
    if (this.rules.has(rule.name)) {
      this.logger.warn(`规则 "${rule.name}" 已存在，将被覆盖`);
    }

    this.rules.set(rule.name, rule);
    
    // 设置默认配置
    if (!this.configs.has(rule.name)) {
      this.configs.set(rule.name, {
        enabled: rule.enabled,
        severity: rule.severity,
      });
    }

    this.logger.info(`注册规则: ${rule.name}`, {
      category: rule.category,
      severity: rule.severity,
      enabled: rule.enabled,
    });
  }

  /**
   * 注销规则
   */
  unregister(ruleName: string): boolean {
    const removed = this.rules.delete(ruleName);
    this.configs.delete(ruleName);
    
    if (removed) {
      this.logger.info(`注销规则: ${ruleName}`);
    }
    
    return removed;
  }

  /**
   * 获取规则
   */
  get(ruleName: string): Rule | undefined {
    return this.rules.get(ruleName);
  }

  /**
   * 获取所有规则
   */
  getAll(): Rule[] {
    return Array.from(this.rules.values());
  }

  /**
   * 获取已启用的规则
   */
  getEnabled(): Rule[] {
    return Array.from(this.rules.values()).filter(rule => {
      const config = this.configs.get(rule.name);
      return config?.enabled ?? rule.enabled;
    });
  }

  /**
   * 按类别获取规则
   */
  getByCategory(category: string): Rule[] {
    return Array.from(this.rules.values()).filter(rule => rule.category === category);
  }

  /**
   * 按标签获取规则
   */
  getByTag(tag: string): Rule[] {
    return Array.from(this.rules.values()).filter(rule => 
      rule.tags?.includes(tag) ?? false
    );
  }

  /**
   * 设置规则配置
   */
  configure(ruleName: string, config: Partial<RuleConfig>): void {
    const existingConfig = this.configs.get(ruleName) || { enabled: true };
    const newConfig = { ...existingConfig, ...config };
    
    this.configs.set(ruleName, newConfig);
    
    this.logger.info(`配置规则: ${ruleName}`, newConfig);
  }

  /**
   * 获取规则配置
   */
  getConfig(ruleName: string): RuleConfig | undefined {
    return this.configs.get(ruleName);
  }

  /**
   * 批量配置规则
   */
  configureMultiple(configs: Record<string, Partial<RuleConfig>>): void {
    Object.entries(configs).forEach(([ruleName, config]) => {
      this.configure(ruleName, config);
    });
  }

  /**
   * 启用规则
   */
  enable(ruleName: string): void {
    this.configure(ruleName, { enabled: true });
  }

  /**
   * 禁用规则
   */
  disable(ruleName: string): void {
    this.configure(ruleName, { enabled: false });
  }

  /**
   * 检查规则是否存在
   */
  has(ruleName: string): boolean {
    return this.rules.has(ruleName);
  }

  /**
   * 获取规则数量
   */
  size(): number {
    return this.rules.size;
  }

  /**
   * 清空所有规则
   */
  clear(): void {
    const count = this.rules.size;
    this.rules.clear();
    this.configs.clear();
    
    this.logger.info(`清空所有规则，共 ${count} 个`);
  }

  /**
   * 获取规则统计信息
   */
  getStats(): {
    total: number;
    enabled: number;
    disabled: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
  } {
    const allRules = this.getAll();
    const enabledRules = this.getEnabled();
    
    const byCategory: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    
    allRules.forEach(rule => {
      // 按类别统计
      byCategory[rule.category] = (byCategory[rule.category] || 0) + 1;
      
      // 按严重程度统计
      const config = this.configs.get(rule.name);
      const severity = config?.severity || rule.severity;
      bySeverity[severity] = (bySeverity[severity] || 0) + 1;
    });
    
    return {
      total: allRules.length,
      enabled: enabledRules.length,
      disabled: allRules.length - enabledRules.length,
      byCategory,
      bySeverity,
    };
  }
}

// 全局规则注册表实例
const ruleRegistry = new RuleRegistry();

/**
 * 注册规则的便捷函数
 */
export function registerRule(rule: Rule): void {
  ruleRegistry.register(rule);
}

/**
 * 获取规则集
 */
export const ruleSet = ruleRegistry.getAll();

// 导出规则注册表实例
export { ruleRegistry };
export default ruleRegistry;